/**
 * Our own price for ONE of a given unit, resolved from the `Item Price` rows that actually exist.
 *
 * ---- Why not `Item.base_price` ----
 *
 * `items.get_items_by_codes` answers ONE `base_price` per item with no unit on it. Measured on
 * 1707050028 (2026-09-14): the `Item Price` table holds `1.00 AED / PKT` and `40.00 AED / CTN`
 * on the same list, and the method returned `base_price: 40.0` beside `stock_uom: PKT` — the
 * carton price labelled as a packet. Scaling that by the pack factor, which is what the RFQ
 * page did first, turned it into 1,600 a carton. The only honest source is the rows themselves,
 * one per (list, unit), which is what `useItemPriceRows` reads.
 *
 * ---- Precedence ----
 *
 *   1. the PREFERRED list — the one the enquiry's delivery location implies (Akhil, 2026-09-14:
 *      the location's `price_list` "passed in to get the stock option");
 *   2. any BUYING list — a real cost beats a better-matching selling price;
 *   3. any other list.
 *
 * Within the chosen list: the row for the unit asked, else a row for a unit the master can
 * convert from — `rate ÷ factor(row unit) × factor(unit asked)` — reported as `derived` so the
 * screen can say "40 × 1.00/PKT". A unit no row can reach answers `null`, never another unit's
 * number: that is exactly the 40/PKT defect this replaces.
 */

import { uomFactor } from './format'
import type { Item } from './types'

export interface PriceRow {
  price_list: string
  rate: number
  currency: string
  uom: string
  /** The row sits on a `Price List` flagged buying — an in-house cost, not a selling price. */
  buying: boolean
}

export interface InHousePrice {
  amount: number
  currency: string
  /** The unit `amount` is per — always the unit asked for. */
  uom: string
  price_list: string
  buying: boolean
  /** True when no row exists for the unit and the figure was converted from another unit's row. */
  derived: boolean
  /** The row it came from, for the explanation. */
  from: { rate: number; uom: string }
}

const round2 = (n: number): number => Math.round(n * 100) / 100

function resolveWithin(
  rows: readonly PriceRow[],
  item: Pick<Item, 'base_uom' | 'uom_conversions'>,
  uom: string,
): InHousePrice | null {
  const priced = rows.filter((r) => Number.isFinite(r.rate) && r.rate > 0)
  const exact = priced.find((r) => r.uom === uom)
  if (exact) {
    return { amount: exact.rate, currency: exact.currency, uom, price_list: exact.price_list, buying: exact.buying, derived: false, from: { rate: exact.rate, uom } }
  }
  const want = uomFactor(item, uom)
  if (want === null) return null
  // The base unit's row first — one conversion, not two — then anything else convertible.
  const ordered = [...priced].sort((a, b) => (a.uom === item.base_uom ? -1 : 0) - (b.uom === item.base_uom ? -1 : 0))
  for (const r of ordered) {
    const have = uomFactor(item, r.uom)
    if (have === null || have <= 0) continue
    return {
      amount: round2((r.rate / have) * want),
      currency: r.currency,
      uom,
      price_list: r.price_list,
      buying: r.buying,
      derived: true,
      from: { rate: r.rate, uom: r.uom },
    }
  }
  return null
}

export function inHousePriceFor(
  rows: readonly PriceRow[] | undefined,
  item: Pick<Item, 'base_uom' | 'uom_conversions'>,
  uom: string,
  preferredList: string | null | undefined,
): InHousePrice | null {
  if (!rows || rows.length === 0) return null
  const preferred = (preferredList ?? '').trim()
  const tiers: PriceRow[][] = [
    preferred ? rows.filter((r) => r.price_list === preferred) : [],
    rows.filter((r) => r.buying && r.price_list !== preferred),
    rows.filter((r) => !r.buying && r.price_list !== preferred),
  ]
  for (const tier of tiers) {
    if (tier.length === 0) continue
    const found = resolveWithin(tier, item, uom)
    if (found) return found
  }
  return null
}
